import { hubFetch } from '@core/auth/services/hub-fetch';
import { inject } from '@angular/core';
import { environment } from '@env/environment';
import { Auth } from '@core/auth/services/auth';
import { EquipmentType, EquipmentListResponse } from '@features/catalog/models';

/**
 * Base Equipment Service
 * Provides shared CRUD operations for all equipment types (modules, inverters, storages)
 * API Endpoints: /organizations/{orgId}/equipment/{type}
 *
 * This service should be extended by specific equipment services
 */
export abstract class BaseEquipmentService<
  T,
  CreateDto = Partial<T>,
  UpdateDto = Partial<T>
> {
  protected readonly baseApiUrl = environment.apiUrl;
  protected readonly auth = inject(Auth);

  /**
   * Build the API URL for equipment endpoints
   * @param organizationId Organization ID
   * @param equipmentType Type of equipment (modules, inverters, storages)
   * @param path Additional path segments
   */
  protected buildUrl(
    organizationId: string,
    equipmentType: EquipmentType,
    path: string = ''
  ): string {
    return `${this.baseApiUrl}/organizations/${organizationId}/equipment/${equipmentType}${path}`;
  }

  /**
   * Get authorization headers with token from cookie
   */
  protected getAuthHeaders(): HeadersInit {

    return {
      'Content-Type': 'application/json',

    };
  }

  /**
   * Handle API errors consistently
   */
  protected async handleApiError(response: Response): Promise<never> {
    let errorData: Record<string, unknown>;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: 'Ein Fehler ist aufgetreten' };
    }

    const error: Error & { status?: number; data?: unknown } = new Error(
      (errorData['error'] as Record<string, string>)?.['message'] || 'API Anfrage fehlgeschlagen'
    );
    error.status = response.status;
    error.data = errorData;
    throw error;
  }

  /**
   * Generic fetch wrapper with error handling
   */
  protected async fetchApi<R>(
    url: string,
    options: RequestInit = {}
  ): Promise<R> {
    const response = await hubFetch(url, {
      headers: this.getAuthHeaders(),
      ...options,
    });

    if (!response.ok) {
      await this.handleApiError(response);
    }

    if (response.status === 204) {
      return undefined as R;
    }

    return response.json();
  }

  /**
   * Get all equipment of a specific type
   * Returns both global and organizational equipment
   */
  async getAll(
    organizationId: string,
    equipmentType: EquipmentType
  ): Promise<EquipmentListResponse<T>> {
    return this.fetchApi<EquipmentListResponse<T>>(
      this.buildUrl(organizationId, equipmentType)
    );
  }

  /**
   * Get a specific equipment item by ID
   */
  async getById(
    organizationId: string,
    equipmentType: EquipmentType,
    id: string
  ): Promise<T> {
    return this.fetchApi<T>(
      this.buildUrl(organizationId, equipmentType, `/${id}`)
    );
  }

  /**
   * Create new equipment
   */
  async create(
    organizationId: string,
    equipmentType: EquipmentType,
    data: CreateDto
  ): Promise<T> {
    return this.fetchApi<T>(this.buildUrl(organizationId, equipmentType), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update existing equipment
   */
  async update(
    organizationId: string,
    equipmentType: EquipmentType,
    id: string,
    data: UpdateDto
  ): Promise<T> {
    return this.fetchApi<T>(
      this.buildUrl(organizationId, equipmentType, `/${id}`),
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Delete equipment (soft delete)
   */
  async delete(
    organizationId: string,
    equipmentType: EquipmentType,
    id: string
  ): Promise<void> {
    return this.fetchApi<void>(
      this.buildUrl(organizationId, equipmentType, `/${id}`),
      {
        method: 'DELETE',
      }
    );
  }

  /**
   * Sync global equipment from Solar Insight.
   * The response shape differs per equipment type, so subclasses declare it.
   */
  async sync<R>(
    organizationId: string,
    equipmentType: EquipmentType
  ): Promise<R> {
    return this.fetchApi<R>(
      this.buildUrl(organizationId, equipmentType, '/sync'),
      {
        method: 'POST',
      }
    );
  }
}
